/**
 * The reports client — spec §M and §G8; T-3.10, T-6.7.
 *
 * Seven reports, one payload shape. Everything a screen needs to render one
 * generically is in it:
 *
 * * `table` — columns and rows, already formatted. **The same rows the CSV
 *   carries**, which is what makes "what you see is what you export" a property
 *   rather than two renderers agreeing by luck;
 * * `caveats` — what the report could not answer and why. Rendered, always: a
 *   zero that is really a gap is the thing a firm would otherwise act on;
 * * the report's own structured fields, for the panels that are not a table.
 *
 * **The index is the authority on what may be opened.** It comes from the same
 * `REPORTS` definition the service gates on, so a screen never has to decide
 * which role sees revenue — it renders what came back, and `meta.unavailable`
 * says which permission would open the rest.
 */

import { ApiError, ORG_HEADER, api, getOrgId } from '../api'
import { SERVICE_API } from '../config'
import { session } from '../session'

export type ReportKey =
  | 'compliance'
  | 'receivables'
  | 'collections'
  | 'productivity'
  | 'pipeline'
  | 'revenue'
  | 'sales'

export interface ReportSummary {
  key: ReportKey
  title: string
  summary: string
}

export interface ReportUnavailable {
  key: ReportKey
  title: string
  /** The pairs this caller is short of — what an administrator would grant. */
  needs: Array<{ resource: string; verb: string }>
}

export interface ReportTable {
  name: string
  columns: string[]
  rows: string[][]
}

export interface ReportPayload {
  key: ReportKey
  title: string
  generated_at: string
  filters: Record<string, string>
  table: ReportTable
  caveats: string[]
  /** Everything else the report states — different per key, read by the
   *  panels in `routes/Reports/`. */
  [field: string]: unknown
}

export interface ReportFilters {
  from?: string
  to?: string
  client_id?: string
  service_id?: string
  branch_id?: string
  period?: string
}

function query(filters: ReportFilters): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value)
  }
  const rendered = params.toString()
  return rendered ? `?${rendered}` : ''
}

export const reports = {
  index: () => api<ReportSummary[]>('/api/v1/reports'),
  open: (key: ReportKey, filters: ReportFilters = {}) =>
    api<ReportPayload>(`/api/v1/reports/${key}${query(filters)}`),
}

/**
 * The CSV, fetched **authenticated**.
 *
 * A plain `<a href>` cannot do it: the route is behind a bearer and the org
 * header and a browser navigation carries neither, so the link would download a
 * 401 named like a report. The same shape `documents.downloadZip` uses, and for
 * the same reason.
 */
export async function downloadCsv(key: ReportKey, filters: ReportFilters = {}): Promise<Blob> {
  const orgId = getOrgId()
  if (!orgId) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
  const params = query({ ...filters })
  const separator = params ? '&' : '?'
  const res = await session.authed(
    `${SERVICE_API}/api/v1/reports/${key}${params}${separator}format=csv`,
    { headers: { [ORG_HEADER]: orgId } },
  )
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'That report could not be exported.',
      res.status,
    )
  }
  return res.blob()
}

export function reportIndexMeta(meta: Record<string, unknown> | undefined): {
  total: number
  unavailable: ReportUnavailable[]
} {
  return {
    total: typeof meta?.total === 'number' ? meta.total : 0,
    unavailable: Array.isArray(meta?.unavailable)
      ? (meta.unavailable as ReportUnavailable[])
      : [],
  }
}

// -- pure helpers, so the screens' wording is testable ------------------------

/**
 * A decimal string as rupees — `16240.00` → `₹16,240.00`.
 *
 * **Never `Number(value)`.** The string is handed to `Intl` whole, for the
 * reason `lib/api/billing.ts: rupees` gives at length: a rupee amount that has
 * been through a JavaScript `number` is one that may have been rounded, and the
 * places it shows up here are a firm's receivables and its revenue.
 *
 * Formatted on the **tiles only**. The table underneath is rendered from
 * `payload.table.rows` exactly as the CSV writes them — plain decimals, no
 * symbol — because that is the property the one-table design buys: what is on
 * the screen is what comes out of the export, cell for cell. A screen that
 * prettied the table would break it.
 */
export function inr(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // @ts-expect-error — `Intl` accepts a decimal string and TypeScript's lib
    // types have not caught up. `Number(value)` here is the rounding this
    // whole helper exists to avoid.
  }).format(value)
}

/**
 * Headline figures per report, as `[label, value]` pairs.
 *
 * **Read off the payload, never recomputed.** Every number here was decided by
 * the service against the ledger; a screen that summed a column to produce its
 * own total would be a second arithmetic, and the one on screen is the one a
 * partner would quote.
 */
export function headlines(report: ReportPayload): Array<[string, string]> {
  const data = report as Record<string, never> & ReportPayload
  switch (report.key) {
    case 'receivables': {
      const reconciliation = (data.reconciliation ?? {}) as Record<string, string>
      return [
        ['Outstanding', inr(String(data.outstanding ?? '0.00'))],
        ['Advance held', inr(String(data.advance_held ?? '0.00'))],
        ['Ledger balance', inr(reconciliation.ledger_balance ?? '0.00')],
      ]
    }
    case 'compliance': {
      const byState = (data.by_state ?? {}) as Record<string, number>
      return [
        ['Obligations', String(data.total ?? 0)],
        ['Overdue', String(byState.overdue ?? 0)],
        ['Not started', String(byState.not_started ?? 0)],
        ['Completed', String(byState.completed ?? 0)],
      ]
    }
    case 'collections':
      return [
        ['Collected', inr(String(data.total ?? '0.00'))],
        ['Receipts', String(data.count ?? 0)],
      ]
    case 'productivity': {
      const totals = (data.totals ?? {}) as Record<string, string | number>
      return [
        ['Completed', String(totals.completed ?? 0)],
        ['Hours', String(totals.hours ?? '0.00')],
        ['Overdue', String(totals.overdue ?? 0)],
      ]
    }
    case 'pipeline':
      return [
        ['Leads', String(data.total ?? 0)],
        ['Won', String(data.won ?? 0)],
        ['Conversion', `${String(data.conversion_rate ?? '0.0')}%`],
      ]
    case 'revenue':
      return [
        ['Billed', inr(String(data.billed ?? '0.00'))],
        ['Credited', inr(String(data.credited ?? '0.00'))],
        ['Net', inr(String(data.net ?? '0.00'))],
      ]
    case 'sales': {
      const overview = (data.overview ?? {}) as Record<string, string | number>
      return [
        ['Billed', inr(String(overview.billed ?? '0.00'))],
        ['Collected', inr(String(overview.collected ?? '0.00'))],
        ['Outstanding', inr(String(overview.outstanding ?? '0.00'))],
        ['Overdue', inr(String(overview.overdue ?? '0.00'))],
      ]
    }
  }
}

/** Which filters a report takes — mirrors `ACCEPTS` in `app/domain/reports.py`.
 *
 *  The service **refuses** a filter a report does not accept rather than
 *  ignoring it, so a screen that offered one would produce a 400 on a control
 *  the user pressed deliberately. This is what hides the control instead. */
export const FILTERS: Record<ReportKey, Array<keyof ReportFilters>> = {
  compliance: ['client_id', 'service_id', 'period', 'from', 'to'],
  receivables: ['client_id'],
  collections: ['from', 'to'],
  productivity: ['from', 'to', 'branch_id'],
  pipeline: ['from', 'to'],
  revenue: ['from', 'to'],
  sales: ['from', 'to'],
}

export function accepts(key: ReportKey, filter: keyof ReportFilters): boolean {
  return FILTERS[key].includes(filter)
}

/** `2026-09-11T06:15:00+00:00` → `11 Sep 2026, 11:45`, in the firm's own day. */
export function generatedLabel(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
}

/** The refusal an index row carries, as a sentence somebody can act on. */
export function unavailableReason(row: ReportUnavailable): string {
  if (!row.needs.length) return 'Your role does not include this report.'
  const needs = row.needs.map((n) => `${n.resource}:${n.verb}`).join(', ')
  return `Needs ${needs} — an administrator in your organisation grants it.`
}
